import axios, { AxiosInstance } from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:7000/api";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers.authorization = token;
          // config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.api.post("/api/auth/login", {
      email,
      password,
    });
    return response.data;
  }

  // User endpoints
  async getUserDetails() {
    const response = await this.api.get("/api/v1/users/details");
    return response.data;
  }

  // EmbryoScope endpoints
  async fetchEmbryoScopePatientsList(params: any) {
    const response = await this.api.get(`/api/v1/patients/list`, {
      params: { ...params },
    });
    return response.data;
  }

  async fetchAccounts(params: any) {
    const response = await this.api.get(`/api/v1/accounts/list?is_all=1`, {
      params: { ...params },
    });
    return response.data;
  }

  async fetchTreatments(id: any) {
    const response = await this.api.get(`/api/v1/treatments/list`, {
      params: { is_all: 1, patient_id: id },
    });
    return response.data;
  }

  async fetchSamples(params: any) {
    const response = await this.api.get(`/api/v1/samples/list`, {
      params: { ...params },
    });
    return response.data;
  }

  async fetchVideoUrl(sample_uuid: any) {
    const response = await this.api.get(`/api/v1/videos/es-video-link`, {
      params: { sample_uuid: sample_uuid },
    });
    return response.data;
  }

  async fetchEmbryoImages(sample_uuid: any) {
    const response = await this.api.get(`/api/v1/images/list`, {
      params: { sample_uuid: sample_uuid },
    });
    return response.data;
  }

  async downloadPatientsExcel(params: any) {
    const response = await this.api.get(`/api/v1/patients/generate-excel`, {
      params: { ...params },
      responseType: "blob",
    });
    return response.data;
  }

  async downloadImage(imageUrl: string) {
    // If it's a relative URL, use your API base URL
    // If it's an absolute URL from your server, fetch with credentials
    const response = await this.api.get(imageUrl, {
      responseType: "blob", // Important: get blob response
    });

    return response.data;
  }

  // ==================== GERI ENDPOINTS ====================

  // Dish Records (Patients List)
  async fetchGeriDishRecords(params: any) {
    const response = await this.api.get(`/api/v1/geri/dish-records/list`, {
      params: { ...params },
    });
    return response.data;
  }

  async downloadGeriDishRecordsExcel() {
    const response = await this.api.get(
      `/api/v1/geri/dish-records/generate-excel`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  }

  // Session Records
  async fetchGeriSessionRecords(params: any) {
    const response = await this.api.get(`/api/v1/geri/session-records/list`, {
      params: { ...params },
    });
    return response.data;
  }

  // Fate Records (Wells/Embryos)
  async fetchGeriFateRecords(params: any) {
    const response = await this.api.get(`/api/v1/geri/fate-records/list`, {
      params: { ...params },
    });
    return response.data;
  }

  async fetchGeriVideoUrl(session_uuid: string, well_number: number) {
    const response = await this.api.get(`/api/v1/videos/geri-video-link`, {
      params: { session_uuid, well_number },
    });
    return response.data;
  }

  async fetchGeriImages(session_uuid: string, well_number: number) {
    const response = await this.api.get(`/api/v1/geri/images/list`, {
      params: { session_uuid, well_number },
    });
    return response.data;
  }
}

export default new ApiService();
